import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

export type NavigationBlockTransaction = {
  /** Continue the blocked navigation after confirmation. */
  retry: () => void;
  /** Cancel: for PUSH/REPLACE no-op; for POP after revert-first, clear pending only. */
  revert: () => void;
  action: 'PUSH' | 'REPLACE' | 'POP';
};

type HistoryState = { idx?: number } | null;

function getHistoryIdx(): number {
  const state = window.history.state as HistoryState;
  return typeof state?.idx === 'number' ? state.idx : 0;
}

/**
 * Blocks SPA navigation when `when` is true (BrowserRouter + MemoryRouter).
 *
 * PUSH/REPLACE: patch navigator before URL changes.
 * Programmatic POP (navigator.go): block before apply when possible.
 * Browser toolbar POP (popstate): revert-first to source, then confirm;
 * Leave replays the original delta once.
 */
export function useNavigationBlocker(
  when: boolean,
  onBlock: (tx: NavigationBlockTransaction) => void,
): void {
  const { navigator } = useContext(NavigationContext);
  const whenRef = useRef(when);
  const onBlockRef = useRef(onBlock);
  const allowNextPopRef = useRef(false);
  const lastIndexRef = useRef(getHistoryIdx());
  const pendingPopDeltaRef = useRef<number | null>(null);

  whenRef.current = when;
  onBlockRef.current = onBlock;

  useEffect(() => {
    const push = navigator.push.bind(navigator);
    const replace = navigator.replace.bind(navigator);
    const go = navigator.go.bind(navigator);

    navigator.push = (...args: Parameters<typeof navigator.push>) => {
      if (!whenRef.current) {
        push(...args);
        lastIndexRef.current = getHistoryIdx();
        return;
      }
      onBlockRef.current({
        action: 'PUSH',
        retry: () => {
          push(...args);
          lastIndexRef.current = getHistoryIdx();
        },
        revert: () => {
          /* URL never changed */
        },
      });
    };

    navigator.replace = (...args: Parameters<typeof navigator.replace>) => {
      if (!whenRef.current) {
        replace(...args);
        lastIndexRef.current = getHistoryIdx();
        return;
      }
      onBlockRef.current({
        action: 'REPLACE',
        retry: () => {
          replace(...args);
          lastIndexRef.current = getHistoryIdx();
        },
        revert: () => {
          /* URL never changed */
        },
      });
    };

    navigator.go = (delta: number) => {
      if (!whenRef.current || allowNextPopRef.current) {
        allowNextPopRef.current = false;
        go(delta);
        lastIndexRef.current = getHistoryIdx();
        return;
      }
      if (delta === 0) {
        go(delta);
        return;
      }
      pendingPopDeltaRef.current = delta;
      onBlockRef.current({
        action: 'POP',
        retry: () => {
          const replay = pendingPopDeltaRef.current ?? delta;
          pendingPopDeltaRef.current = null;
          allowNextPopRef.current = true;
          go(replay);
          lastIndexRef.current = getHistoryIdx();
        },
        revert: () => {
          pendingPopDeltaRef.current = null;
        },
      });
    };

    const onPopState = (event: PopStateEvent) => {
      const nextIdx =
        event.state && typeof event.state === 'object' && 'idx' in event.state
          ? Number((event.state as { idx: number }).idx)
          : getHistoryIdx();
      const delta = nextIdx - lastIndexRef.current;

      if (allowNextPopRef.current) {
        allowNextPopRef.current = false;
        lastIndexRef.current = nextIdx;
        pendingPopDeltaRef.current = null;
        return;
      }

      if (!whenRef.current) {
        lastIndexRef.current = nextIdx;
        pendingPopDeltaRef.current = null;
        return;
      }

      if (delta === 0) {
        lastIndexRef.current = nextIdx;
        return;
      }

      // Browser toolbar Back/Forward: URL already moved — revert-first.
      pendingPopDeltaRef.current = delta;
      allowNextPopRef.current = true;
      go(-delta);

      onBlockRef.current({
        action: 'POP',
        retry: () => {
          const replay = pendingPopDeltaRef.current ?? delta;
          pendingPopDeltaRef.current = null;
          allowNextPopRef.current = true;
          go(replay);
          lastIndexRef.current = getHistoryIdx();
        },
        revert: () => {
          pendingPopDeltaRef.current = null;
        },
      });
    };

    window.addEventListener('popstate', onPopState);
    lastIndexRef.current = getHistoryIdx();

    return () => {
      navigator.push = push;
      navigator.replace = replace;
      navigator.go = go;
      window.removeEventListener('popstate', onPopState);
    };
  }, [navigator]);
}
